import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { CLIENTS_API_ENDPOINTS } from "../constants/clients.constants";
import type {
  InternalClientsTreeResponse,
  ClientsListResponse,
  ApiClientDetail,
  ApiClientListItem,
  ApiOrganizationClientProfile,
  ClientByIdResponse,
  CreateClientBody,
  CreateOrganizationBody,
  CreateOrganizationResponse,
  UpdateClientBody,
  UpdateOrganizationBody,
  OrganizationDetailResponse,
  OrganizationsSearchResponse,
  ClientAccount,
  CreateClientAccountRequest,
  SendKycVerificationMailRequest,
} from "../types/clients-api.types";
import type { ClientsFiltersState } from "../types/clients.types";
import { normalizeClientDetail, normalizeOrganizationClientProfile } from "../utils/normalizeClient";

export interface InternalClientsListParams {
  skip: number;
  limit: number;
  type?: string;
  kyc_status?: string;
  country?: string;
  search?: string;
  organization_id?: string;
  verified_only?: boolean;
}

export function buildClientsQueryParams(
  filters: ClientsFiltersState,
  page: number,
  pageSize: number,
  organizationId?: string,
): InternalClientsListParams {
  const skip = (page - 1) * pageSize;
  const params: InternalClientsListParams = { skip, limit: pageSize };
  if (filters.client_type?.trim()) params.type = filters.client_type.trim();
  if (filters.kyc_status?.trim()) params.kyc_status = filters.kyc_status.trim();
  if (filters.country?.trim()) params.country = filters.country.trim();
  const searchTrimmed = filters.search?.trim();
  if (searchTrimmed) params.search = searchTrimmed;
  if (organizationId) params.organization_id = organizationId;
  return params;
}

function buildQueryString(params: InternalClientsListParams): string {
  const search = new URLSearchParams();
  search.set("skip", String(params.skip));
  search.set("limit", String(params.limit));
  if (params.type) search.set("type", params.type);
  if (params.kyc_status) search.set("kyc_status", params.kyc_status);
  if (params.country) search.set("country", params.country);
  if (params.search) search.set("search", params.search);
  if (params.organization_id) search.set("organization_id", params.organization_id);
  if (params.verified_only === true) search.set("verified_only", "true");
  return search.toString();
}

/**
 * Unified client service — all endpoints use /internal/clients. The backend auto-scopes by organization_id
 * from the JWT token.
 */
export class ClientsService {
  // ── List ────────────────────────────────────────────────────────────

  /** Tree view (admin): organizations as root nodes with nested clients */
  static async fetchTree(
    params: InternalClientsListParams,
  ): Promise<AxiosResponse<InternalClientsTreeResponse>> {
    const url = `${CLIENTS_API_ENDPOINTS.TREE}?${buildQueryString(params)}`;
    return HttpApiService.get<InternalClientsTreeResponse>(url);
  }

  /** Flat list: GET /internal/clients (auto-scoped by JWT organization_id) */
  static async fetchList(params: InternalClientsListParams): Promise<AxiosResponse<ClientsListResponse>> {
    const url = `${CLIENTS_API_ENDPOINTS.LIST}?${buildQueryString(params)}`;
    return HttpApiService.get<ClientsListResponse>(url);
  }

  static async searchOrganizations(params: {
    name?: string;
    skip?: number;
    limit?: number;
  }): Promise<AxiosResponse<OrganizationsSearchResponse>> {
    const search = new URLSearchParams();
    search.set("skip", String(params.skip ?? 0));
    search.set("limit", String(params.limit ?? 50));
    if (params.name?.trim()) search.set("name", params.name.trim());
    const url = `${CLIENTS_API_ENDPOINTS.ORGANIZATIONS_SEARCH}?${search.toString()}`;
    return HttpApiService.get<OrganizationsSearchResponse>(url);
  }

  // ── CRUD ────────────────────────────────────────────────────────────

  /** Create a client: POST /internal/clients */
  static async create(body: CreateClientBody): Promise<AxiosResponse<ApiClientListItem>> {
    return HttpApiService.post<ApiClientListItem, CreateClientBody>(CLIENTS_API_ENDPOINTS.LIST, body);
  }

  /**
   * Create a top-level organization (admin): POST /internal/organizations. qa contract: the body carries the
   * full corporate `identity` (BusinessIdentityDto); the backend find-or-creates it, links it to the org and
   * registers it with the KYC provider atomically. Returns the Organization entity (no KYC link — issued
   * separately through VerificationService.issueLink()).
   */
  static async createOrganization(
    body: CreateOrganizationBody,
  ): Promise<AxiosResponse<CreateOrganizationResponse>> {
    return HttpApiService.post<CreateOrganizationResponse, CreateOrganizationBody>(
      CLIENTS_API_ENDPOINTS.ORGANIZATIONS,
      body,
    );
  }

  /** Client detail: GET /internal/clients/:id — raw nested response normalized to the flat view model. */
  static async fetchById(id: string): Promise<AxiosResponse<ClientByIdResponse>> {
    const response = await HttpApiService.get<ApiClientDetail>(CLIENTS_API_ENDPOINTS.BY_ID(id));
    return { ...response, data: normalizeClientDetail(response.data) };
  }

  /** Update client (tenant contact data only): PATCH /internal/clients/:id */
  static async update(id: string, body: UpdateClientBody): Promise<AxiosResponse<ApiClientDetail>> {
    return HttpApiService.patch<ApiClientDetail, UpdateClientBody>(CLIENTS_API_ENDPOINTS.BY_ID(id), body);
  }

  /** Soft delete client: DELETE /internal/clients/:id */
  static async deleteClient(id: string): Promise<AxiosResponse<unknown>> {
    return HttpApiService.delete<unknown>(CLIENTS_API_ENDPOINTS.BY_ID(id));
  }

  /** Soft delete organization: DELETE /internal/organizations/:id */
  static async deleteOrganization(id: string): Promise<AxiosResponse<unknown>> {
    return HttpApiService.delete<unknown>(CLIENTS_API_ENDPOINTS.ORGANIZATION_DETAIL_BY_ID(id));
  }

  /** Update organization: PATCH /internal/organizations/:id */
  static async updateOrganization(id: string, body: UpdateOrganizationBody): Promise<AxiosResponse<unknown>> {
    return HttpApiService.patch<unknown, UpdateOrganizationBody>(
      CLIENTS_API_ENDPOINTS.ORGANIZATION_DETAIL_BY_ID(id),
      body,
    );
  }

  // ── Accounts ────────────────────────────────────────────────────────

  /** List accounts: GET /internal/clients/:clientId/accounts */
  static async fetchAccounts(clientId: string): Promise<AxiosResponse<ClientAccount[]>> {
    return HttpApiService.get<ClientAccount[]>(CLIENTS_API_ENDPOINTS.ACCOUNTS(clientId));
  }

  /** Create account: POST /internal/clients/:clientId/accounts */
  static async createAccount(
    clientId: string,
    body: CreateClientAccountRequest,
  ): Promise<AxiosResponse<ClientAccount>> {
    return HttpApiService.post<ClientAccount, CreateClientAccountRequest>(
      CLIENTS_API_ENDPOINTS.ACCOUNTS(clientId),
      body,
    );
  }

  /** Delete account: DELETE /internal/clients/:clientId/accounts/:id */
  static async deleteAccount(clientId: string, accountId: string): Promise<AxiosResponse<unknown>> {
    return HttpApiService.delete<unknown>(CLIENTS_API_ENDPOINTS.ACCOUNT_BY_ID(clientId, accountId));
  }

  // ── Organization tree endpoints (admin only) ────────────────────────

  /** Organization detail (tree admin): GET /internal/clients/organizations/:id */
  static async fetchOrganizationById(
    organizationId: string,
  ): Promise<AxiosResponse<OrganizationDetailResponse>> {
    return HttpApiService.get<OrganizationDetailResponse>(
      CLIENTS_API_ENDPOINTS.ORGANIZATION_BY_ID(organizationId),
    );
  }

  /**
   * Organization rendered as a client detail with its REAL corporate KYC data (status, verified_at,
   * documents): GET /internal/clients/organizations/:id/client-profile. The tree detail endpoint hardcodes
   * `kyc_status: null` at the org level on qa, so KYC-aware views must use this one.
   */
  static async fetchOrganizationClientProfile(
    organizationId: string,
  ): Promise<AxiosResponse<ClientByIdResponse>> {
    const response = await HttpApiService.get<ApiOrganizationClientProfile>(
      CLIENTS_API_ENDPOINTS.ORGANIZATION_CLIENT_PROFILE(organizationId),
    );
    return { ...response, data: normalizeOrganizationClientProfile(response.data) };
  }

  /** Organization accounts (tree admin) */
  static async fetchOrganizationAccounts(organizationId: string): Promise<AxiosResponse<ClientAccount[]>> {
    return HttpApiService.get<ClientAccount[]>(CLIENTS_API_ENDPOINTS.ORGANIZATION_ACCOUNTS(organizationId));
  }

  /** Create organization account (tree admin): POST /internal/organizations/:id/accounts */
  static async createOrganizationAccount(
    organizationId: string,
    body: CreateClientAccountRequest,
  ): Promise<AxiosResponse<ClientAccount>> {
    return HttpApiService.post<ClientAccount, CreateClientAccountRequest>(
      CLIENTS_API_ENDPOINTS.ORGANIZATION_ACCOUNTS(organizationId),
      body,
    );
  }

  /** Delete organization account (tree admin): DELETE /internal/organizations/:id/accounts/:accountId */
  static async deleteOrganizationAccount(
    organizationId: string,
    accountId: string,
  ): Promise<AxiosResponse<unknown>> {
    return HttpApiService.delete<unknown>(
      CLIENTS_API_ENDPOINTS.ORGANIZATION_ACCOUNT_BY_ID(organizationId, accountId),
    );
  }

  /** Send KYC verification email — POST /internal/mail/send */
  static async sendKycVerificationMail(
    body: SendKycVerificationMailRequest,
  ): Promise<AxiosResponse<unknown>> {
    return HttpApiService.post<unknown, SendKycVerificationMailRequest>(
      CLIENTS_API_ENDPOINTS.MAIL_SEND,
      body,
    );
  }
}
